<template>
    <div class="packlist" :class="{ 'packlist-desktop': isDesktop }">
        <!-- Mobile/Tablet Layout -->
        <div v-if="!isDesktop" class="packlist-mobile">
            <!-- Tablet: BackButton + PacklistItems row, Mobile: Just PacklistHeader -->
            <div class="mobile-tablet-header-section">
                <div v-if="isTablet" class="tablet-back-and-items">
                    <BackButton layout="tablet" class="back-button-tablet-container" />
                    <div class="tablet-packlist-items-container">
                        <PacklistItems 
                            v-if="packlistItems && currentOrder"
                            :currentOrderId="currentOrderId"
                            :openItems="openItems"
                            :packedItems="packedItems"
                            :pausedItems="pausedItems"
                            @item-click="handlePacklistItemClick"
                        />
                    </div>
                </div>
                <PacklistHeader
                    v-if="currentOrder" 
                    :currentOrderId="currentOrderId"
                    :currentOrder="currentOrder"
                    :currentFlyer="currentFlyer"
                    :currentPackstats="currentPackstats"
                    :openItems="openItems"
                    :packedItems="packedItems"
                    :pausedItems="pausedItems"
                    :upsellItems="upsellItems"
                    :currentCarton="currentCarton"
                    @packlist-item-click="handlePacklistItemClick"
                    @open-problem-modal="openModal('problem')"
                />
            </div>
            <PacklistBody 
                :articles="currentProducts"
                :packlistItems="packlistItems"
                :layout="articleLayout"
                :isMulticart="isMulticart"
                :hasMissingItems="hasMissingItems"
                :isWarranty="isWarranty"
                :warrantyReason="warrantyReason"
                :productsCount="currentProducts.length"
                :currentOrderId="currentOrderId"
                @reset-article="resetArticle"
                @open-warranty-modal="openModal('warranty')"
            />
            <PacklistActions
                :hasMissingItems="hasMissingItems"
                :orderIsMulticart="isMulticart"
                :currentPacklistItem="currentPacklistItem"
                :currentPrinter="currentPrinter"
                :currentPacklistName="this.$route.params.id"
                :currentOrder="currentOrder"
                :labelPrinted="labelPrinted"
                :isPrinting="isPrinting"
                :isItemPacked="isCurrentItemPacked"
                :workflow="workflow"
                @open-multicart-modal="openModal('multicart')"
                @open-settings-modal="openModal('settings')"
                @print-label="printLabel"
                @pack="handlePack"
                @handle-fullscreen="handleFullscreen"
            />
        </div>

        <!-- Desktop Layout (L-shaped) -->
        <div v-else class="packlist-desktop-layout">
            <!-- Top section with sidebar, header column, and articles -->
            <div class="packlist-desktop-top">
                <!-- Left Sidebar - Packlist Items -->
                <div class="packlist-sidebar">
                    <PacklistItems 
                        v-if="currentOrder"
                        :currentOrderId="currentOrderId"
                        :openItems="openItems"
                        :packedItems="packedItems"
                        :pausedItems="pausedItems"
                        :isVertical="true"
                        @item-click="handlePacklistItemClick"
                    />
                </div>
                
                <!-- Middle Column - Header Content -->
                <div class="packlist-header-column">
                    <div v-if="currentOrder" class="packlist-header-content-desktop">
                        <!-- Top Section: OrderId, Customer, Upsell Items -->
                        <div class="header-top-section">
                            <!-- Customer Info - Column Layout -->
                            <div class="packlist-header-customer-info-desktop">
                                <div class="customer-info-column">
                                    <!-- Back Button for Desktop -->
                                    <BackButton layout="desktop" />
                                    
                                    <div class="order-id-section">
                                        <span class="info-label">Bestellung / Kunde:</span>
                                        <div class="order-id-section-content">
                                            <v-chip color="primary" small class="order-id-chip">
                                                <span class="packlist-header-order-id">{{ currentOrderId }}</span>
                                            </v-chip>
                                            <v-chip
                                                color="grey"
                                                small
                                                outlined
                                            >
                                                <v-icon left small>mdi-package-variant</v-icon>
                                                Offen - {{ openItems.length }}
                                            </v-chip>
                                        </div>
                                        <div class="customer-details">
                                            <span class="packlist-header-customer-name">{{ currentOrder?.customer?.firstName }} {{ currentOrder?.customer?.lastName }}</span>
                                            <span class="packlist-header-customer-address">{{ currentOrder?.customer?.adress }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Carton Item (Desktop) -->
                            <CartonItem
                                v-if="currentCarton"
                                :currentCarton="currentCarton" 
                            />
                            
                            <!-- Upsell Items -->
                            <UpsellItems 
                                :upsellItems="upsellItems"
                                :currentFlyer="currentFlyer"
                            />
                        </div>
                        
                        <!-- Bottom Section: Timer, Problem Button -->
                        <div class="header-bottom-section">
                            <!-- Timer -->
                            <Timer :currentPackstats="currentPackstats" />
                            
                            <!-- Problem Button -->
                            <v-btn
                                color="error"
                                class="problem-button-header"
                                @click="openModal('problem')"
                                small
                            >
                                Problem melden
                            </v-btn>
                        </div>
                    </div>
                </div>
                
                <!-- Right Side - Articles Area -->
                <div class="packlist-articles-section">
                    <PacklistBody 
                        :articles="currentProducts"
                        :packlistItems="packlistItems"
                        :layout="articleLayout"
                        :isMulticart="isMulticart"
                        :hasMissingItems="hasMissingItems"
                        :isDesktop="true"
                        :isWarranty="isWarranty"
                        :warrantyReason="warrantyReason"
                        :productsCount="currentProducts.length"
                        :currentOrderId="currentOrderId"
                        @reset-article="resetArticle"
                        @open-warranty-modal="openModal('warranty')"
                    />
                </div>
            </div>
            
            <!-- Bottom Actions -->
            <div class="packlist-actions-section">
                <PacklistActions
                    :hasMissingItems="hasMissingItems"
                    :orderIsMulticart="isMulticart"
                    :currentPacklistItem="currentPacklistItem"
                    :currentPrinter="currentPrinter"
                    :currentPacklistName="this.$route.params.id"
                    :currentOrder="currentOrder"
                    :labelPrinted="labelPrinted"
                    :isPrinting="isPrinting"
                    :isItemPacked="isCurrentItemPacked"
                    :workflow="workflow"
                    @open-multicart-modal="openModal('multicart')"
                    @open-settings-modal="openModal('settings')"
                    @print-label="printLabel"
                    @pack="handlePack"
                    @handle-fullscreen="handleFullscreen"
                />
            </div>
        </div>

        <PacklistModals 
            :packlistName="packlistName"
            :currentModal="currentModal"
            :currentArticleToReset="currentArticleToReset"
            :currentPrinter="currentPrinter"
            :multicartPayload="multicartPayload"
            :currentPacklistItems="packlistItems"
            :currentOrderId="currentOrderId"
            :currentOrder="currentOrder"
            :openPrinterTab="openPrinterTab"
            :user="user"
            :warrantyReason="warrantyReason"
            :warrantyNote="warrantyNote"
            @close-modal="closeModal"
            @select-printer="selectPrinter"
            @confirm-multicart="handleMulticartConfirm"
            @confirm-storage="handleMulticartStorage"
            @layout-changed="handleLayoutChanged"
            @report-problem="handleProblemReport"
            @refresh-packlist="handleRefreshPacklist"
        />

        <DamagedDialog />
        
        <!-- Loading Overlay -->
        <div v-if="isLoadingPacklist || isLoadingItem" class="loading-overlay">
            <div class="loading-content">
                <div class="loading-spinner"></div>
                <p class="loading-text">
                    {{ isLoadingPacklist ? 'Packliste wird geladen...' : 'Lädt Bestellung...' }}
                </p>
            </div>
        </div>

        <!-- Packlist Alert Dialog -->
        <PacklistAlertDialog
            v-model="alertDialog"
            :alert="currentAlert"
            @dismissed="onAlertDismissed"
        />
    </div>
</template>

<script>
import PacklistHeader from './components/PacklistHeader.vue'
import PacklistBody from './components/PacklistBody.vue'
import PacklistActions from './components/PacklistActions.vue'
import PacklistModals from './components/PacklistModals.vue'
import PacklistItems from './components/PacklistItems.vue'
import UpsellItems from './components/UpsellItems.vue'
import Timer from './components/Timer.vue'
import DamagedDialog from './components/DamagedDialog.vue'
import BackButton from './components/BackButton.vue'
import CartonItem from './components/CartonItem.vue'

//Services
import PacklistService from '@/services/PacklistService.js';
import AuthService from '@/services/AuthService.js';
import EventBus from "@/event-bus";

export default {
    name: 'PacklistRoot',
    components: {
        PacklistHeader,
        PacklistActions,
        PacklistBody,
        PacklistModals,
        PacklistItems,
        UpsellItems,
        Timer,
        DamagedDialog,
        BackButton,
        CartonItem,
        PacklistAlertDialog: () => import('./components/PacklistAlertDialog.vue')
    },
    data() {
        return {
            packlistItems: null,
            currentPacklistItemIndex: 0,
            currentOrderId: null,
            currentPacklistItemId: null,
            currentOrder: null,
            currentPacklistItem: null,
            // Prevent infinite loops
            _processingIndexChange: false,
            currentPackstats: {
                progress: 0,
                targetProgress: 40,
                count: 0,
                time: 0,
                packStart: null,
                packEnd: null
            },
            currentModal: 'none',
            currentArticleToReset: null,
            currentPrinter: null,
            multicartPayload: null,
            user: null,
            isLoadingItem: false,
            isLoadingPacklist: true,
            isPrinting: false,
            labelPrinted: false,
            printButtonPressed: false,
            under8cm: false,
            articleLayout: 'linear',
            openPrinterTab: false,
            windowWidth: window.innerWidth,
            packlistName: null,
            itemViewStartTime: null, // Time when item was first loaded/viewed
            packingStarted: false,   // Whether actual packing has started
            upsellWhitelist: [
                "LYTO_TYPE_C_KABEL",
                "LYTO_NETZSTECKER",
                "LYTO_LED_SOCKEL_WHITE",
                "wooden_led_base_HV",
                "wooden_led_base_RGB",
                "NETZSTECKER",
                "TYP_C_USB_KABEL",
                "gravurfarbe_kinderbesteck",
                "PRIO_ORDER",
                "LS24_SAMTBEUTEL_RED",
                "LS24_SAMTBEUTEL_SILVER",
                "LS24_SAMTBEUTEL_BLACK",
                "Samtbeutel (schwarz)",
                "Samtbeutel (rot)",
                "GF_Organzasäckchen",
                "GF_Samtsäckchen",
                "hauck_holzleiste_5mm",
                "upsell_holzsockel5mm",
                "stoffbeutel"
            ],
            currentCarton: null,
            alertDialog: false,
            currentAlert: null,
            flyerMapping: [
                {
                    value: 'GF',
                    label: 'Flyer',
                    bg: '#d45355',
                    color: '#f9f9f9',
                    url: 'https://geschenkfreude.com/cdn/shop/t/19/assets/geschenkfreude-logo.svg?v=152746364003842334961738143808'
                },
                {
                    value: 'MM',
                    label: 'Flyer',
                    bg: '#d0e5de',
                    color: '#121212',
                    url: 'https://muckelmeister.de/cdn/shop/t/86/assets/logo-white-border.webp?v=4545666206715883731744100060'
                },
                {
                    value: '#BP',
                    label: 'Flyer',
                    bg: '#EBE2D2',
                    color: '#121212',
                    url: 'https://boandbirdie.com/cdn/shop/files/Bo_Birdie_Lockup_Black.png?v=1714989905&width=1368'
                },
                {
                    value: '#FW',
                    label: 'Flyer',
                    bg: '#E17D63',
                    color: '#f9f9f9',
                    url: 'https://famwalls.com/cdn/shop/files/famwalls_test_logo.png?v=1637591278&width=200'
                }
            ]
        }
    },
    computed: {
        messageSocket() {
            return this.$store.state.messages?.socket;
        },
        isWarranty() {
            return this.currentPacklistItem?.warranty;
        },
        warrantyReason() {
            return this.currentPacklistItem?.warrantyReason;
        },
        warrantyNote() {
            return this.currentPacklistItem?.warrantyNote;
        },
        openItems() {
            if (!this.packlistItems || !Array.isArray(this.packlistItems)) {
                return [];
            }
            
            return this.packlistItems.filter(item => {
                // Enhanced validation to prevent "Cannot read property of undefined" errors
                return item && 
                       typeof item === 'object' && 
                       item.status && 
                       (item.status === 'open' || item.status === 'paused');
            });
        },
        pausedItems() {
            if (!this.packlistItems || !Array.isArray(this.packlistItems)) {
                return [];
            }
            
            return this.packlistItems.filter(item => {
                return item && 
                       typeof item === 'object' && 
                       item.status === 'paused';
            });
        },
        packedItems() {
            if (!this.packlistItems || !Array.isArray(this.packlistItems)) {
                return [];
            }
            
            return this.packlistItems
            .filter(item => {
                return item && 
                       typeof item === 'object' && 
                       item.status === 'packed';
            })
            .sort((a, b) => {
                // If both have packEnd dates, compare them
                if (a.packEnd && b.packEnd) {
                    const dateA = new Date(a.packEnd);
                    const dateB = new Date(b.packEnd);
                    return dateB - dateA; // Newest first
                }
                
                // If a has packEnd but b doesn't, a comes after
                if (a.packEnd && !b.packEnd) {
                return -1;
                }
                
                // If b has packEnd but a doesn't, b comes after
                if (!a.packEnd && b.packEnd) {
                return 1;
                }
                
                // If neither has packEnd, maintain original order
                return 0;
            });
        },
        upsellItems() {
            if (!this.currentOrder?.products || !Array.isArray(this.currentOrder.products)) return [];
            
            //Filter products that are upsell=true AND !isEvent OR in upsellWhitelist
            const filteredUpsellItems = this.currentOrder.products.filter(product => {
                // Validate product object before accessing properties
                if (!product || typeof product !== 'object' || !product.sku) {
                    return false;
                }
                
                return (product.upsell === true && !product.isEvent) || this.upsellWhitelist.includes(product.sku);
            });

            //Deduplicate products
            const deduplicatedProducts = filteredUpsellItems.reduce((acc, product) => {
                const existingProduct = acc.find(p => p.sku === product.sku);
                
                if (existingProduct) {
                    existingProduct.qty = (existingProduct.qty || 1) + (product.qty || 1);
                } else {
                    acc.push({
                        ...product,
                        qty: product.qty || 1
                    });
                }
                
                return acc;
            }, []);

            console.log('upsellItems filtered:', deduplicatedProducts);
            return deduplicatedProducts;
        },
        currentProducts() {
            if (!this.currentOrder?.products || !Array.isArray(this.currentOrder.products)) return [];
            
            let dontShow = ["customization"];

            const filtered = this.currentOrder.products.filter(product => {
                // Validate product object before accessing properties
                if (!product || typeof product !== 'object' || !product.sku) {
                    return false;
                }
                
                const isUpsell = Boolean(product.upsell) === true;
                const isEvent = Boolean(product.isEvent) === true;
                const shouldInclude = (!isUpsell && !isEvent) || (isUpsell && isEvent);
                const notInUpsellWhitelist = !this.upsellWhitelist.includes(product.sku);
                const notDontShow = !dontShow.includes(product.sku);

                return shouldInclude && notInUpsellWhitelist && notDontShow;
            });

            console.log('currentProducts filtered:', filtered);
            return filtered;
        },
        currentFlyer() {
            try {
                if (!this.currentOrder) {
                    return null;
                }
                
                const orderId = this.currentOrder.orderId;
                const reference = this.currentOrder.reference;
                let value = null;
                
                if (!reference && orderId) {
                    if (orderId.toString().startsWith('MM')) {
                        value = this.flyerMapping.find(f => f.value === 'MM');
                    } else if (isNaN(orderId) && !orderId.toString().startsWith('#')) {
                        value = this.flyerMapping.find(f => f.value === 'GF');
                    }
                }

                // If external order -> check for reference
                if (reference) {
                    if (reference.toString().startsWith('#BP')) {
                        value = this.flyerMapping.find(f => f.value === '#BP');
                    } else if (reference.toString().startsWith('#FW')) {
                        value = this.flyerMapping.find(f => f.value === '#FW');
                    } else {
                        console.log('No match for reference conditions');
                    }
                }

                return value;
            } catch (error) {
                console.error('ERROR in currentFlyer computed property:', error);
                console.error('Error stack:', error.stack);
                return null;
            }
        },
        isMulticart() {
            // Return false if currentOrder or products are not loaded yet
            if (!this.currentOrder?.products || this.currentProducts.length === 0) {
                return false;
            }

            // Check if any product has isMultiCart property set to true
            return this.currentProducts.some(product => product.isMultiCart === true);
        },
        isDesktop() {
            // Optimized desktop detection for tablets and small laptops
            const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isWideScreen = this.windowWidth >= 900; // Lowered threshold for small laptops
            const isVeryWideScreen = this.windowWidth >= 1200;
            const isTabletLandscape = hasTouch && this.windowWidth >= 1024 && this.windowWidth <= 1366;
            const isTabletPortrait = hasTouch && this.windowWidth >= 768 && this.windowWidth < 1024;
            
            // Very wide screens are always desktop
            if (isVeryWideScreen) return true;
            
            // Tablet in landscape should use desktop layout for better product visibility
            if (isTabletLandscape) return true;
            
            // Tablet in portrait can use desktop layout if screen is wide enough (>= 900px)
            if (isTabletPortrait && this.windowWidth >= 900) return true;
            
            // Non-touch devices with wide screens (laptops)
            if (!hasTouch && isWideScreen) return true;
            
            return false;
        },
        isTablet() {
            // Enhanced tablet detection with orientation awareness
            const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isTabletWidth = this.windowWidth >= 768 && this.windowWidth < 1200;
            const isMobileWidth = this.windowWidth < 768;
            
            // Must have touch capability to be considered tablet
            if (!hasTouch) return false;
            
            // Too small to be tablet
            if (isMobileWidth) return false;
            
            // If desktop layout is active, don't consider it a tablet for layout purposes
            if (this.isDesktopLayout) return false;
            
            return isTabletWidth;
        },
        isMobile() {
            // Mobile: small screen with or without touch
            return this.windowWidth < 768;
        },
        isDesktopLayout() {
            // Helper to determine if desktop layout should be used
            return this.isDesktop;
        },
        hasMissingItems() {
            if (!this.currentOrder || !this.currentOrder.products || !Array.isArray(this.currentOrder.products)) {
                return false;
            }
            return this.currentOrder.products.some(x => 
                x && 
                typeof x === 'object' && 
                x.status === 'missing'
            );
        },
        isPrinted() {
            return this.printButtonPressed;
        },
        workflow() {
            return {
                canPrint: this.currentPrinter && !this.isPrinting,
                canPack: this.labelPrinted && this.currentOrder && !this.isPrinting
            };
        },
        isCurrentItemPacked() {
            // Check if the current item is already packed
            if (!this.currentPacklistItemId || !this.packlistItems || !Array.isArray(this.packlistItems)) {
                return false;
            }
            
            const currentItem = this.packlistItems.find(item => 
                item && 
                typeof item === 'object' && 
                item.id === this.currentPacklistItemId
            );
            
            return currentItem?.status === 'packed';
        }
    },
    methods: {
        // Alert handlers
        handlePacklistAlert(alert) {
            console.log('📢 Received packlist alert:', alert);
            this.currentAlert = alert;
            this.alertDialog = true;
        },
        onAlertDismissed() {
            this.alertDialog = false;
            this.currentAlert = null;
        },

        //Data functions and handlers
        async getPacklist() {
            const packlistName = this.$route.params.id;

            if (!packlistName) {
                return;
            }

            try {
                const response = await PacklistService.getPacklistItems(packlistName);

                if (response.status === 200 && response.data.length > 0) {
                    if (this.packlistItems) {
                        this.packlistItems.splice(0, this.packlistItems.length, ...response.data);
                    } else {
                        this.$set(this, 'packlistItems', response.data);
                    }
                } else if (response.status === 200 && response.data.length === 0) {
                    this.$router.push('/');
                }
            } catch (error) {
                console.error(error);
            }
        },
        async getCurrentPacklistItem(orderId, id) {
            try {
                const response = await PacklistService.getPacklistItem(orderId);

                if (response.status === 200) {
                    this.currentOrder = response.data;
                    this.currentPacklistItem = response.data;
                    if (id) {
                        this.currentPacklistItemId = id;

                    }
                } 
            } catch (error) {
                console.error('Error getting current packlist item:', error);
            }
        },
        async handlePackstats() {
            // Only store the view start time, don't actually start tracking yet
            const currentPackSession = localStorage.getItem('lastTimestamp');

            if (currentPackSession && this.user?.id === JSON.parse(currentPackSession).userId) {
                const packSession = JSON.parse(currentPackSession);
                this.itemViewStartTime = new Date(packSession.packStart);
                this.currentPackstats.packStart = packSession.packStart;
                console.log('Using existing session start time for display', this.currentPacklistItemId, packSession.packStart);
            } else {
                const viewStartTime = new Date();
                this.itemViewStartTime = viewStartTime;
                
                const newPackSession = {
                    userId: this.user?.id,
                    packStart: viewStartTime
                };

                this.currentPackstats.packStart = newPackSession.packStart;
                localStorage.setItem('lastTimestamp', JSON.stringify(newPackSession));
                console.log('Stored new view start time (not tracking yet)', this.currentPacklistItemId, viewStartTime);
            }
            
            // Reset packing started flag for new item
            this.packingStarted = false;
            await this.startActualPacking();
        },
        
        async startActualPacking() {
            // This is called on first pack attempt
            if (!this.packingStarted && this.itemViewStartTime && this.currentPacklistItemId) {
                console.log('🏁 Starting actual pack tracking with stored time:', this.currentPacklistItemId, this.itemViewStartTime);
                await PacklistService.trackStart(this.currentPacklistItemId, this.itemViewStartTime);
                this.packingStarted = true;
                console.log('✅ Pack tracking started successfully');
            } else {
                console.log('⏭️ Packing already started or no start time available', this.packingStarted, this.itemViewStartTime, this.currentPacklistItemId);
            }
        },
        async loadPackStats() {
            try {
                const response = await PacklistService.getPackStats();
                if (response?.data) {
                    this.currentPackstats.count = response.data.count;
                    this.currentPackstats.time = response.data.time;
                }
            } catch (error) {
                console.error('Error loading pack stats:', error);
            }
        },
        async printLabel(under8cm = false) {
            if (!this.currentPrinter) {
                this.openPrinterTab = true;
                this.openModal('settings');
                return;
            }

            if (this.isPrinting) {
                return;
            }

            this.isPrinting = true;

            try {
                await PacklistService.print(this.currentOrderId, this.currentPrinter.url, under8cm);
                this.labelPrinted = true;
                this.printButtonPressed = true;
                
                // Reset packing started flag so user can start fresh tracking on first pack
                console.log('📄 Label printed, ready for packing to start tracking');
            } catch (error) {
                console.error('Error printing label:', error);
            } finally {
                this.isPrinting = false;
            }
        },
        async pack() {
            try {
                console.log('📦 Pack button pressed - starting pack process');
                
                // Start actual packing tracking if this is the first time
                
                
                console.log('🏁 Calling trackEnd for item:', this.currentPacklistItemId);
                await PacklistService.trackEnd(this.currentPacklistItemId);

                // Store the current index before refreshing
                const currentOrderId = this.currentOrderId;
                const currentIndex = this.packlistItems ? this.packlistItems.findIndex(item => 
                    item.id === this.currentPacklistItemId
                ) : -1;

                // Set loading before refreshing data
                this.isLoadingItem = true;

                // Refresh the packlist
                await this.getPacklist();

                // Reset print button (like old version)
                this.labelPrinted = false;
                this.printButtonPressed = false;

                // Find the next open item after our current position
                let nextItem = null;
                
                // First try to find open items with the same order ID (for multi-item orders)
                const sameOrderOpenItems = this.packlistItems ? this.packlistItems.filter(item => 
                    item && item.status === 'open' && 
                    item.orderId === currentOrderId &&
                    item.id !== this.currentPacklistItemId
                ) : [];
                
                if (sameOrderOpenItems.length > 0) {
                    // If there are still open items from the same order, pick the first one
                    nextItem = sameOrderOpenItems[0];
                } else {
                    // Otherwise, find the next open item in the sequence
                    const nextOpenItems = this.packlistItems ? this.packlistItems
                        .filter(item => item && item.status === 'open')
                        .filter(item => {
                            const itemIndex = this.packlistItems.indexOf(item);
                            return itemIndex > currentIndex;
                        }) : [];
                        
                    if (nextOpenItems.length > 0) {
                        nextItem = nextOpenItems[0];
                    } else if (this.openItems && this.openItems.length > 0) {
                        // If no next items found, use the first open item as fallback
                        nextItem = this.openItems[0];
                    }
                }
                
                if (nextItem) {
                    // Update properties
                    this.currentOrderId = nextItem.orderId;
                    this.currentPacklistItemId = nextItem.id;
                    this.currentPacklistItem = null; // Clear first to ensure reactivity
                    
                    // Get detailed item data
                    await this.getCurrentPacklistItem(nextItem.orderId, nextItem.id);
                    
                    // Update the index to match the new current item
                    this.currentPacklistItemIndex = this.openItems ? this.openItems.findIndex(item => item && item.id === nextItem.id) : -1;
                }

                // Update pack stats for next item (but don't start tracking yet)
                const newPackSession = {
                    userId: this.user ? this.user.id : null,
                    packStart: new Date()
                };
                this.currentPackstats.packStart = newPackSession.packStart;
                localStorage.setItem('lastTimestamp', JSON.stringify(newPackSession));
                
                // Store view start time for next item but don't track yet
                this.itemViewStartTime = newPackSession.packStart;
                this.packingStarted = false; // Reset for next item
                
                await this.loadPackStats();
                
                // Finally turn off loading
                this.isLoadingItem = false;

            } catch (error) {
                console.error('Error packing item:', error);
                this.isLoadingItem = false;
            }
        },
        async handleMulticartConfirm() {
            if (!this.currentPrinter) {
                this.openPrinterTab = true;
                this.openModal('settings');
                return;
            }

            try {
                //Get labelcount how much labels should be printed
                const labelCount = this.packlistItems ? this.packlistItems.filter(item => item.orderId === this.currentOrderId).length : 1;

                const response = await PacklistService.requestMultiCartSlot(
                    this.currentOrderId,
                    { printer: this.currentPrinter.url, labelCount: labelCount }
                );

                if (response.status === 200) {
                    this.multicartPayload = response.data;
                    this.openModal('multicart-confirm');
                }
            } catch (error) {
                console.error('Error requesting multicart slot:', error);
            }
        },
        async handleMulticartStorage(payload) {
            try {
                console.log('Handling multicart storage:', payload);

                try {
                    const response = await PacklistService.putMultiCartItems(
                        this.currentOrderId,
                        payload
                    );

                    if (response.status === 200) {
                        this.closeModal();
                        
                        // Store current order ID before refresh
                        const currentOrderId = this.currentOrderId;
                        
                        // Refresh the packlist data
                        await this.getPacklist();
                        
                        // Find the next item to navigate to (skip items from same order)
                        const nextItem = this.openItems.find(item => item.orderId !== currentOrderId);
                        
                        if (nextItem) {
                            // Navigate to the next item
                            this.currentOrderId = nextItem.orderId;
                            this.currentPacklistItemId = nextItem.id;
                            this.currentPacklistItem = null;
                            
                            // Update the index
                            this.currentPacklistItemIndex = this.openItems.findIndex(item => item.id === nextItem.id);
                            
                            // Load the complete item data
                            await this.getCurrentPacklistItem(nextItem.orderId, nextItem.id);
                        } else if (this.openItems.length > 0) {
                            // Fallback: if no different order found, just take the first available item
                            const fallbackItem = this.openItems[0];
                            this.currentOrderId = fallbackItem.orderId;
                            this.currentPacklistItemId = fallbackItem.id;
                            this.currentPacklistItem = null;
                            this.currentPacklistItemIndex = 0;
                            
                            // Load the complete item data
                            await this.getCurrentPacklistItem(fallbackItem.orderId, fallbackItem.id);
                        } else {
                            // No more open items, redirect to main page
                            console.log('🏁 No more orders available, redirecting to main page');
                            this.$router.push('/');
                        }
                    }
                } catch (error) {
                    console.error('Error putting multicart slot:', error);
                }
            } catch (error) {
                console.error('Error handling multicart storage:', error);
            }
        },

        //UI Functions and Handlers
        resetItemSpecificState() {
            // Reset all item-specific state when switching items
            this.labelPrinted = false;
            this.isPrinting = false;
            this.printButtonPressed = false;
            this.currentModal = 'none';
            this.currentArticleToReset = null;
            this.multicartPayload = null;
            this.packingStarted = false;  // Reset packing state for new item
            this.itemViewStartTime = null; // Will be set on next item load
            this.currentCarton = null;
        },
        next() {
            const currentOrderId = this.currentOrderId;
            let nextIndex = this.currentPacklistItemIndex + 1;
            
            // Find the next item with a different order ID
            while (nextIndex < this.openItems.length && this.openItems[nextIndex]?.orderId === currentOrderId) {
                nextIndex++;
            }
            
            // If we found a valid next item with different order ID, use it
            if (nextIndex < this.openItems.length) {
                this.currentPacklistItemIndex = nextIndex;
            }
        },
        prev() {
            const currentOrderId = this.currentOrderId;
            let prevIndex = this.currentPacklistItemIndex - 1;
            
            // Find the previous item with a different order ID
            while (prevIndex >= 0 && this.openItems[prevIndex]?.orderId === currentOrderId) {
                prevIndex--;
            }
            
            // If we found a valid previous item with different order ID, use it
            if (prevIndex >= 0) {
                this.currentPacklistItemIndex = prevIndex;
            }
        },
        handleKeydown(event) {
            // Guard against undefined event
            if (!event || !event.key) {
                console.warn('handleKeydown called with invalid event:', event);
                return;
            }
            
            if (event.key === 'ArrowRight') {
                this.next();
            } else if (event.key === 'ArrowLeft') {
                this.prev();
            } else if (event.key === 'b') {
                if (this.workflow.canPrint) {
                    this.printLabel();
                }
            } else if (event.key === 'c' && this.workflow.canPack) {
                this.pack();
            } else if (event.key === 'a') {
                this.under8cm = !this.under8cm;
                localStorage.setItem('under8cm', JSON.stringify(this.under8cm));
            }
        },
        handlePacklistItemClick(item) {
            //Add loading overlay
            this.isLoadingItem = true;
            
            // Reset all item-specific state when switching items
            this.resetItemSpecificState();

            this.currentOrderId = item.orderId;
            this.currentPacklistItemId = item.id;
            this.currentPacklistItem = item;

            // Note: currentCarton will be set in currentOrder watcher
        },
        handleFullscreen() {
            try {
                if (!document.fullscreenElement) {
                    this.isFullscreen = true;
                    // Try different fullscreen methods for better compatibility
                    if (document.documentElement.requestFullscreen) {
                        document.documentElement.requestFullscreen();
                    } else if (document.documentElement.mozRequestFullScreen) {
                        document.documentElement.mozRequestFullScreen();
                    } else if (document.documentElement.webkitRequestFullscreen) {
                        document.documentElement.webkitRequestFullscreen();
                    } else if (document.documentElement.msRequestFullscreen) {
                        document.documentElement.msRequestFullscreen();
                    } else {
                        console.warn('Fullscreen API not supported on this device');
                        this.isFullscreen = false;
                    }
                } else {
                    this.isFullscreen = false;
                    if (document.exitFullscreen) {
                        document.exitFullscreen();
                    } else if (document.mozCancelFullScreen) {
                        document.mozCancelFullScreen();
                    } else if (document.webkitExitFullscreen) {
                        document.webkitExitFullscreen();
                    } else if (document.msExitFullscreen) {
                        document.msExitFullscreen();
                    }
                }
            } catch (error) {
                console.warn('Fullscreen operation failed:', error);
                this.isFullscreen = false;
            }
        },
        openModal(modalId) {
            this.currentModal = modalId;
        },
        closeModal() {
            this.currentModal = 'none';
        },
        resetArticle(article) {
            // ONLY products that are part of the packlist (not in shelf, not missing)
            const packlistProducts = this.currentProducts.filter(product => 
                !product.shelf && // Not already in multicart shelf
                product.status !== 'missing' // Not missing
            );
            
            const dialogData = {
                orderId: this.currentOrder.orderId,
                items: packlistProducts
            };
            
            console.log('📋 Opening reset dialog with', packlistProducts.length, 'items for selection');
            console.log('📋 Available items in modal:', packlistProducts.map(p => ({ sku: p.sku, status: p.status, hasShelf: !!p.shelf })));

            EventBus.$emit("OPEN_DAMAGED_DIALOG_RESETONLY", dialogData);
        },
        selectPrinter(printer) {
            this.currentPrinter = printer;
            this.openPrinterTab = false; // Reset the flag after selection
        },
        handleLayoutChanged(newLayout) {
            this.articleLayout = newLayout;
        },
        handleWindowResize() {
            this.windowWidth = window.innerWidth;
        },
        handlePack() {
            this.pack();
        },
        
        // Problem reporting functions (like old PacklistRoot)
        async handleProblemReport(message) {
            if (!message) return;
            
            try {
                const response = await PacklistService.sendFeedback(this.currentOrderId, message);
                
                if (response.status === 200) {
                    this.closeModal();
                    console.log('Problem reported successfully');
                } else {
                    console.error('Failed to report problem:', response);
                }
            } catch (error) {
                console.error('Error reporting problem:', error);
            }
        },
        
        async handleRefreshPacklist() {
            try {
                await this.getPacklist();
                console.log('Packlist refreshed after problem report');
            } catch (error) {
                console.error('Error refreshing packlist:', error);
            }
        }
    },
    watch: {
        // Watch for socket availability and register alert listener
        messageSocket: {
            handler(socket) {
                if (socket) {
                    console.log('[PacklistRoot] Socket became available, registering alert listener');
                    socket.on('packlist-alert', this.handlePacklistAlert);
                }
            },
            immediate: true
        },
        currentPacklistItemIndex: {
            handler(newVal, oldVal) {
                // Prevent infinite loops by checking if we're already processing a change
                if (this._processingIndexChange) {
                    console.log('🛡️ Preventing recursive currentPacklistItemIndex change');
                    return;
                }
                
                console.log('currentPacklistItemIndex changed:', newVal, 'openItems count:', this.openItems?.length);
                
                // Guard against invalid indices and undefined openItems
                if (!this.openItems || this.openItems.length === 0 || newVal === undefined || newVal === null || newVal < 0) {
                    console.warn('Invalid currentPacklistItemIndex:', newVal, 'openItems:', this.openItems?.length);
                    return;
                }
                
                // Check if index is within bounds
                if (newVal >= this.openItems.length) {
                    console.warn('currentPacklistItemIndex out of bounds:', newVal, 'max:', this.openItems.length - 1);
                    // Auto-correct to last valid index to prevent infinite loops
                    this._processingIndexChange = true;
                    this.currentPacklistItemIndex = Math.max(0, this.openItems.length - 1);
                    this._processingIndexChange = false;
                    return;
                }
                
                this._processingIndexChange = true;
                
                try {
                    // Show loading if this is not the initial load (oldVal exists)
                    if (oldVal !== undefined) {
                        this.isLoadingItem = true;
                    }
                    
                    const currentItem = this.openItems[newVal];
                    
                    // Enhanced validation for the current item
                    if (currentItem && currentItem.id && currentItem.orderId) {
                        console.log('✅ Valid item found at index', newVal, ':', currentItem.id);
                        
                        // Reset all item-specific state when switching items
                        this.resetItemSpecificState();
                        
                        this.currentOrderId = currentItem.orderId;
                        this.currentPacklistItemId = currentItem.id;
                        this.currentPacklistItem = currentItem;
                        
                        // Check if the new item has warranty and auto-open modal
                        if (currentItem.warranty) {
                            console.log('🟠 Warranty item detected - auto-opening warranty modal');
                            this.openModal('warranty');
                        }
                    } else {
                        console.error('❌ Invalid item at index', newVal, ':', currentItem);
                        console.error('Item structure:', JSON.stringify(currentItem, null, 2));
                        
                        // Try to find a valid item to switch to
                        const validItemIndex = this.openItems.findIndex(item => 
                            item && item.id && item.orderId
                        );
                        
                        if (validItemIndex >= 0) {
                            console.log('🔄 Switching to valid item at index:', validItemIndex);
                            this.currentPacklistItemIndex = validItemIndex;
                        } else {
                            console.error('❌ No valid items found in openItems array');
                            // Clear current state to prevent further errors
                            this.currentOrderId = null;
                            this.currentPacklistItemId = null;
                            this.currentPacklistItem = null;
                        }
                    }
                } catch (error) {
                    console.error('❌ Error in currentPacklistItemIndex watcher:', error);
                    console.error('Error details:', {
                        newVal,
                        oldVal,
                        openItemsLength: this.openItems?.length,
                        currentItem: this.openItems?.[newVal]
                    });
                } finally {
                    this._processingIndexChange = false;
                }
            },
            immediate: true
        },
        currentOrderId: {
            handler(newVal) {
                console.log('currentOrderId changed:', newVal);
                if (newVal) {
                    this.getCurrentPacklistItem(newVal, this.currentPacklistItemId);
                }
            },
            deep: true
        },
        currentOrder: {
            async handler(newVal) {
                console.log('currentOrder changed:', newVal ? 'Order loaded' : 'Order is null');
                console.log('currentFlyer will be:', this.currentFlyer);
                
                // Hide loading when order is successfully loaded
                if (newVal) {
                    this.isLoadingItem = false;
                    
                    // Set view start time for the new item (but don't start tracking)
                    if (this.user) {
                        await this.handlePackstats();
                    }
                    
                    // Update currentCarton when order changes
                    if (newVal.carton) {
                        this.currentCarton = newVal.carton;
                        console.log('📦 Current Carton updated:', this.currentCarton);
                    } else {
                        this.currentCarton = null;
                        console.log('📦 No carton data, clearing currentCarton');
                    }
                    
                    // Check for warranty and auto-open modal
                    if (newVal && newVal.warranty) {
                        console.log('🟠 Warranty detected in currentOrder - auto-opening warranty modal');
                        this.openModal('warranty');
                    }
                }
                
                if (newVal?.products) {
                    console.log('Order products:', newVal.products);
                    console.log('Total products count:', newVal.products.length);
                }
            },
            deep: true
        },
        currentProducts: {
            handler(newVal) {
                console.log('currentProducts watcher triggered:', newVal);
                if (newVal && Array.isArray(newVal)) {
                    console.log('currentProducts count:', newVal.length);
                } else {
                    console.warn('currentProducts is not a valid array:', newVal);
                }
            },
            immediate: true
        },
        upsellItems: {
            handler(newVal) {
                console.log('upsellItems watcher triggered:', newVal);
                if (newVal && Array.isArray(newVal)) {
                    console.log('upsellItems count:', newVal.length);
                } else {
                    console.warn('upsellItems is not a valid array:', newVal);
                }
            },
            immediate: true
        },
        currentPrinter: {
            handler(newVal) {
                console.log('currentPrinter watcher triggered:', newVal);
                if (newVal !== undefined && newVal !== null) {
                    localStorage.setItem('currentPrinter', JSON.stringify(newVal));
                }
            }
        }
    },
    async mounted() {
        console.log('🚀 PacklistRoot mounted - starting data loading...');

        // Note: Socket alert listener is registered via watcher
        // This ensures it works even if socket connects after component mounts

        // Listen for EventBus events (like damaged dialog)
        EventBus.$on("REFRESH_PACKLIST", async () => {
            console.log('📡 REFRESH_PACKLIST event received - starting refresh...');
            try {
                const currentOrderId = this.currentOrderId;
                await this.getPacklist();
                console.log('✅ Packlist refresh completed successfully');
                
                // Check if current order still exists after refresh
                if (currentOrderId && this.packlistItems) {
                    const currentOrderStillExists = this.packlistItems.some(item => item.orderId === currentOrderId);
                    
                    if (!currentOrderStillExists && this.openItems.length > 0) {
                        // Current order was completely removed, switch to first available order
                        console.log('🔄 Current order no longer exists, switching to next available order');
                        this.currentOrderId = this.openItems[0].orderId;
                        this.currentPacklistItemId = this.openItems[0].id;
                        this.currentPacklistItemIndex = 0;
                        
                        // Reset state for new order
                        this.resetItemSpecificState();
                        
                        // Load the new current order
                        await this.getCurrentPacklistItem(this.currentOrderId, this.currentPacklistItemId);
                    } else if (!currentOrderStillExists && this.openItems.length === 0) {
                        // No more open orders, redirect to main page
                        console.log('🏁 No more orders available, redirecting to main page');
                        this.$router.push('/');
                    }
                }
            } catch (error) {
                console.error('❌ Error during packlist refresh:', error);
            }
        });

        //Initial states based on device and layout detection
        console.log(`🔍 Device Detection - Width: ${this.windowWidth}px`);
        console.log(`🔍 Touch Support: ${'ontouchstart' in window || navigator.maxTouchPoints > 0}`);
        console.log(`🔍 isTablet: ${this.isTablet}, isDesktop: ${this.isDesktop}, isDesktopLayout: ${this.isDesktopLayout}`);
        
        if (this.isDesktopLayout) {
            console.log('🚀 Initializing desktop layout (includes tablets in landscape/wide portrait)');
            this.articleLayout = 'grid';
            
            // For touch devices using desktop layout (tablets), try fullscreen
            const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            if (hasTouch && this.windowWidth <= 1366) {
                console.log('🚀 Touch device using desktop layout - attempting fullscreen');
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Only try fullscreen if we can detect fullscreen capability
                        if (document.fullscreenEnabled !== false) {
                            console.log('Attempting tablet fullscreen...');
                            this.handleFullscreen();
                        } else {
                            console.warn('Fullscreen not available on this device');
                        }
                    }, 500); // Small delay to ensure page is fully loaded
                });
            }
        } else if (this.isTablet) {
            console.log('🚀 Initializing tablet mobile layout');
            this.articleLayout = 'linear';
            
            // Delayed fullscreen for tablets using mobile layout
            this.$nextTick(() => {
                setTimeout(() => {
                    if (document.fullscreenEnabled !== false) {
                        console.log('Attempting tablet fullscreen...');
                        this.handleFullscreen();
                    } else {
                        console.warn('Fullscreen not available on this device');
                    }
                }, 500);
            });
        } else {
            console.log('🚀 Initializing mobile layout');
            this.articleLayout = 'linear';
        }

        try {
            // Add event listeners for keydown and window resize
            window.addEventListener('keydown', this.handleKeydown);
            window.addEventListener('resize', this.handleWindowResize);

            // Set packlistName from route
            this.packlistName = this.$route.params.id;
            
            //Get Packlistitems on initial load
            await this.getPacklist();

            //Set currentOrderId to the first item in the packlist
            if (this.packlistItems.length > 0 && this.openItems.length > 0) {
                this.currentOrderId = this.openItems[0].orderId;
                this.currentPacklistItemId = this.openItems[0].id;
            }

            //Get the currentPacklistItem
            if (this.currentOrderId) {
                await this.getCurrentPacklistItem(this.currentOrderId, this.currentPacklistItemId);
            }

            // Note: currentCarton is set in currentOrder watcher

            //Check if currentPrinter is set in localStorage
            const currentPrinter = localStorage.getItem('currentPrinter');
            if (currentPrinter) {
                this.currentPrinter = JSON.parse(currentPrinter);
            }
            
            // Get user and initialize timer functionality
            this.user = (await AuthService.getUser()).data;
            await this.loadPackStats();
            if (this.currentPacklistItemId) {
                await this.handlePackstats(); // Sets view start time but doesn't start tracking
            }
            
            // Load saved article layout preference
            const savedLayout = localStorage.getItem('articleLayout');
            if (savedLayout) {
                this.articleLayout = savedLayout;
            }
            
            // Progress is now calculated in real-time in Timer component

            console.log('✅ PacklistRoot fully loaded and ready!');
        } catch (error) {
            console.error('❌ Error during PacklistRoot initialization:', error);
        } finally {
            // Always hide initial loading overlay, even if there's an error
            this.isLoadingPacklist = false;
        }
    },
    beforeDestroy() {
        // Clean up event listeners
        window.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('resize', this.handleWindowResize);

        // Clean up Socket.io listeners
        if (this.messageSocket) {
            console.log('[PacklistRoot] Removing packlist-alert listener');
            this.messageSocket.off('packlist-alert', this.handlePacklistAlert);
        }

        // Clean up EventBus listeners
        EventBus.$off("REFRESH_PACKLIST");
    }
}
</script>

<style>
.packlist {
    box-sizing: border-box;
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile browsers */
    min-height: 100vh; /* Fallback for older browsers */
    position: relative;
    overflow: hidden;
    width: 100vw;
    max-width: 100vw;
}

/* Mobile/Tablet Layout (default) */
.packlist-mobile {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile browsers */
    min-height: 100vh; /* Fallback for older browsers */
    position: relative;
    overflow: hidden;
}

.mobile-tablet-header-section {
    flex-shrink: 0;
}

/* Tablet-specific layout for BackButton + PacklistItems */
.tablet-back-and-items {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 0;
    background-color: #f9f9f9;
    border-bottom: 1px solid #e0e0e0;
}

.back-button-tablet-container {
    flex-shrink: 0;
}

.tablet-packlist-items-container {
    flex: 1;
    overflow-x: auto;
}

/* Desktop Layout (L-shaped) - FORCED FOR ALL DEVICES */
.packlist-desktop-layout {
    display: flex !important;
    flex-direction: column !important;
    height: 100dvh !important;
    width: 100% !important;
    overflow: hidden !important;
}

/* Top section with sidebar, header column, and articles */
.packlist-desktop-top {
    display: flex !important;
    flex: 1 !important;
    height: calc(100dvh - 120px) !important;
    overflow: hidden !important;
}

/* Left Sidebar - Packlist Items */
.packlist-sidebar {
    height: 100% !important;
    background-color: #f9f9f9 !important;
    border-right: 1px solid #e0e0e0 !important;
    flex-shrink: 0 !important;
    overflow: hidden !important;
}

/* Middle Column - Header Content */
.packlist-header-column {
    width: clamp(280px, 25vw, 350px) !important;
    height: 100% !important;
    background-color: #f9f9f9 !important;
    border-right: 1px solid #e0e0e0 !important;
    flex-shrink: 0 !important;
    overflow-y: auto !important;
    padding: 12px !important;
}

.packlist-header-content-desktop {
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    height: 100% !important;
    gap: 12px !important;
}

.header-top-section {
    display: flex !important;
    flex-direction: column !important;
    gap: 14px !important;
    flex: 1 !important;
}

.header-bottom-section {
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
    flex-shrink: 0 !important;
    padding-top: 12px !important;
    border-top: 1px solid #e0e0e0 !important;
}

.packlist-header-customer-info-desktop {
    flex-shrink: 0 !important;
}

.customer-info-column {
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
}

.order-id-section,
.customer-details-section {
    display: flex !important;
    flex-direction: column !important;
    gap: 6px !important;
}

.order-id-section-content {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    flex-wrap: wrap !important;
}

.info-label {
    font-size: 12px !important;
    font-weight: 600 !important;
    color: #666 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
}

.customer-details {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
}

.order-id-chip {
    width: fit-content !important;
    display: inline-flex !important;
}

.packlist-header-order-id {
    font-size: 1.2rem !important;
    font-weight: 600 !important;
}

.problem-button-header {
    margin-top: 6px !important;
    width: 100% !important;
}

.packlist-header-customer-name {
    font-size: 1.2rem !important;
    font-weight: 600 !important;
}

.packlist-header-customer-address {
    font-size: 1.2rem !important;
}

/* Articles Section - Right side in desktop */
.packlist-articles-section {
    flex: 1 !important;
    overflow: hidden !important;
    background-color: #fff !important;
    height: 100% !important;
}

/* Actions Section - Bottom spanning full width */
.packlist-actions-section {
    flex-shrink: 0 !important;
    background-color: #f0f0f0 !important;
    border-top: 1px solid #e0e0e0 !important;
    width: 100% !important;
}



/* Mobile Specific Adjustments */
@media (max-width: 767px) {
    .packlist-mobile {
        height: 100vh;
        height: 100dvh; /* Better mobile browser support */
        min-height: -webkit-fill-available; /* iOS Safari compatibility */
    }
    
    .packlist-actions-section {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        margin: 0;
        padding: 0;
        width: 100%;
    }
}

/* Tablet Mobile Layout (Portrait tablets that don't use desktop layout) */
@media (min-width: 768px) and (max-width: 899px) {
    /* Increase touch targets for tablets */
    .v-btn {
        min-height: 48px !important;
    }
    
    /* Better spacing for tablet use */
    .packlist-header-content {
        padding: 8px !important;
    }
    
    .packlist-mobile {
        height: 100vh;
        height: 100dvh; /* Better mobile browser support */
        min-height: -webkit-fill-available; /* iOS Safari compatibility */
    }
    
    .packlist-actions-section {
        position: sticky;
        bottom: 0;
        left: 0;
        right: 0;
        margin: 0;
        padding: 0;
        width: 100%;
    }
}

/* Small Laptops and Compact Desktop Layout */
@media (min-width: 900px) and (max-width: 1199px) {
    .packlist-header-column {
        width: clamp(240px, 22vw, 280px); /* More compact for smaller screens */
        padding: 10px;
    }
    
    .packlist-desktop-top {
        height: calc(100vh - 100px); /* Slightly smaller actions area */
        height: calc(100dvh - 100px);
    }
    
    .packlist-header-content-desktop {
        gap: 10px; /* Tighter spacing */
    }
    
    /* Optimize for touch if detected */
    .v-btn {
        min-height: 44px !important;
    }
    
    /* More compact info sections */
    .order-id-section,
    .customer-details-section {
        gap: 4px;
    }
    
    .header-top-section {
        gap: 10px;
    }
}

/* Standard Desktop Layout */
@media (min-width: 1200px) and (max-width: 1399px) {
    .packlist-header-column {
        width: clamp(280px, 23vw, 320px);
        padding: 12px;
    }
    
    .packlist-desktop-top {
        height: calc(100vh - 120px);
        height: calc(100dvh - 120px);
    }
    
    .packlist-header-content-desktop {
        gap: 12px;
    }
}

/* Large Desktop Layout */
@media (min-width: 1400px) {
    .packlist-header-column {
        width: clamp(320px, 25vw, 380px); /* Allow more space on very large screens */
        padding: 16px;
    }
    
    .packlist-desktop-top {
        height: calc(100vh - 120px);
        height: calc(100dvh - 120px);
    }
    
    .packlist-header-content-desktop {
        gap: 16px;
    }
}

/* Tablet Landscape Specific Optimizations */
@media (min-width: 1024px) and (max-width: 1366px) and (orientation: landscape) {
    .packlist-header-column {
        width: clamp(220px, 20vw, 260px); /* Very compact for landscape tablets */
        padding: 8px;
    }
    
    .packlist-desktop-top {
        height: calc(100vh - 90px); /* Smaller actions for landscape */
        height: calc(100dvh - 90px);
    }
    
    /* Touch-optimized buttons for tablets */
    .v-btn {
        min-height: 48px !important;
        font-size: 14px !important;
    }
    
    .packlist-header-content-desktop {
        gap: 8px; /* Very compact spacing */
    }
    
    .order-id-section,
    .customer-details-section {
        gap: 3px;
    }
    
    .header-top-section {
        gap: 8px;
    }
    
    /* Smaller text for more content */
    .info-label {
        font-size: 11px;
    }
    
    .packlist-header-order-id {
        font-size: 1.1rem;
    }
    
    .packlist-header-customer-name {
        font-size: 1.1rem;
    }
    
    .packlist-header-customer-address {
        font-size: 1rem;
    }
}

/* Tablet Portrait Desktop Layout */
@media (min-width: 900px) and (max-width: 1023px) and (orientation: portrait) {
    .packlist-header-column {
        width: clamp(200px, 18vw, 240px); /* Very compact for portrait tablets */
        padding: 8px;
    }
    
    .packlist-desktop-top {
        height: calc(100vh - 95px);
        height: calc(100dvh - 95px);
    }
    
    /* Ensure touch targets are adequate */
    .v-btn {
        min-height: 48px !important;
    }
    
    .packlist-header-content-desktop {
        gap: 6px; /* Compact for portrait */
    }
}

/* Loading Overlay */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
}

.loading-content {
    background: white;
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 500;
    color: #333;
}

@keyframes spin {
    0% { 
        transform: rotate(0deg); 
    }
    100% { 
        transform: rotate(360deg); 
    }
}

/* Comprehensive viewport and compatibility fixes */
@supports (height: 100dvh) {
    .packlist,
    .packlist-mobile,
    .packlist-desktop-layout {
        height: 100dvh;
        min-height: 100dvh;
    }
}

/* iOS Safari specific fixes */
@supports (-webkit-appearance: none) {
    .packlist-mobile {
        min-height: -webkit-fill-available;
    }
}

/* Force proper positioning across all browsers */
.packlist-actions-section {
    box-sizing: border-box;
    margin: 0 !important;
    padding: 0 !important;
}

/* Tablet-specific desktop layout fine-tuning */
@media (min-width: 900px) and (max-width: 1366px) and (pointer: coarse) {
    /* Only apply to touch devices in this range (tablets) */
    .packlist-sidebar {
        width: fit-content;
    }
    
    /* Optimize button spacing for touch */
    .v-btn + .v-btn {
        margin-left: 8px !important;
    }
    
    /* Better touch targets */
    .packlist-header-customer-info-desktop .v-chip {
        min-height: 36px !important;
    }
}
</style>